import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

export type NavItemType = {
  to: string;
  title: string;
  icon: any;
  subtitle: string;
  handleClick: () => void;
  isCollapsed?: boolean;
};

export const NavItem = ({ to, title, icon, subtitle, handleClick, isCollapsed = false }: NavItemType) => {
  const location = useLocation();
  const isCurrentPath = location.pathname === to;
  
  if (isCollapsed) {
    return (
      <section className="group mb-1 relative">
        <NavLink
          to={to}
          className={({ isActive }) =>
            `block w-full relative p-3 transition-all duration-300 ease-in-out rounded-xl ${
              isActive
                ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/25'
                : 'text-slate-300 hover:bg-gradient-to-r hover:from-slate-700 hover:to-slate-600 hover:text-white'
            }`
          }
          onClick={handleClick}
          title={title}
        >
          <div className="flex items-center justify-center transition-all duration-300 group-hover:scale-110">
            <span className="text-xl">{icon}</span>
          </div>
        </NavLink>
        
        {/* Tooltip */}
        <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 bg-slate-800 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-50">
          {title}
          <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-slate-800"></div>
        </div>
      </section>
    );
  }
  
  return (
    <section className="group mb-1">
      <NavLink
        to={to}
        className={({ isActive }) =>
          `block w-full relative px-4 py-3 transition-all duration-300 ease-in-out rounded-xl ${
            isActive
              ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/25 transform scale-[1.02]'
              : 'text-slate-300 hover:bg-gradient-to-r hover:from-slate-700 hover:to-slate-600 hover:text-white hover:shadow-lg hover:shadow-slate-700/20 hover:transform hover:scale-[1.02]'
          }`
        }
        onClick={handleClick}
      >
        <div className="flex items-center gap-4 transition-all duration-300 group-hover:translate-x-1">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 group-hover:scale-110 bg-white/10 backdrop-blur-sm">
            <span className="text-xl transition-all duration-300">{icon}</span>
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-sm transition-all duration-300 select-none">{title}</span>
            <span className="text-xs opacity-60 transition-all duration-300">{subtitle}</span>
          </div>
        </div>
        
        {/* Animated border effect */}
        <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 blur-sm"></div>
        </div>
        
        {/* Active indicator */}
        <div className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-2 h-2 rounded-full transition-all duration-300 ${
          isCurrentPath ? 'bg-white shadow-md' : 'bg-transparent'
        }`}></div>
      </NavLink>
    </section>
  );
};